import { supabase } from '../lib/supabase';
import { duelActivityService } from './socialService';

/**
 * Achievement Card Service
 * Handles shareable achievement card generation, streak tracking, and social sharing
 */

/**
 * Achievement types
 */
export const ACHIEVEMENT_TYPES = {
  DUEL_WIN: 'duel_win',
  DUEL_STREAK: 'duel_streak',
  MILESTONE: 'milestone',
  GOAL_COMPLETE: 'goal_complete',
  PERFECT_WEEK: 'perfect_week'
};

/**
 * Get storage key for achievement tracking
 */
const getAchievementKey = (userId, type, identifier) => {
  return `achievement_${userId}_${type}_${identifier}`;
};

/**
 * Check if achievement card was already shown
 */
const isAchievementShown = (userId, type, identifier) => {
  const key = getAchievementKey(userId, type, identifier);
  return localStorage.getItem(key) === 'shown';
};

/**
 * Mark achievement card as shown
 */
const markAchievementShown = (userId, type, identifier) => {
  const key = getAchievementKey(userId, type, identifier);
  localStorage.setItem(key, 'shown');
};

/**
 * Calculate current streak from user profile
 */
const calculateStreak = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('current_streak')?.eq('id', userId)?.single();

    if (error) throw error;
    return data?.current_streak || 0;
  } catch (error) {
    console.error('Error calculating streak:', error);
    return 0;
  }
};

/**
 * Generate social share URLs for different platforms
 */
export const generateShareUrls = (achievement, appUrl = window.location?.origin) => {
  const shareText = encodeURIComponent(
    `🏆 ${achievement?.title}! ${achievement?.description} on VitalTrack`
  );
  const shareUrl = encodeURIComponent(`${appUrl}/social-hub`);
  const hashtags = encodeURIComponent('VitalTrack,Wellness,Achievement');

  return {
    twitter: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}&hashtags=${hashtags}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
    whatsapp: `https://wa.me/?text=${shareText}%20${shareUrl}`,
    copyLink: `${appUrl}/social-hub`
  };
};

export const achievementCardService = {
  /**
   * Check for duel completion achievement
   */
  async checkDuelCompletion(userId, duel) {
    if (!duel || duel?.status !== 'completed' || duel?.winner_id !== userId) {
      return null;
    }

    // Check if already shown
    if (isAchievementShown(userId, ACHIEVEMENT_TYPES?.DUEL_WIN, duel?.id)) {
      return null;
    }

    // Get user stats for streak calculation
    const stats = await duelActivityService?.getUserDuelStats(userId);
    const streak = await calculateStreak(userId);

    const isCreator = duel?.creator_id === userId;
    const userProgress = isCreator ? duel?.creator_progress : duel?.opponent_progress;
    const opponentProgress = isCreator ? duel?.opponent_progress : duel?.creator_progress;
    const opponent = isCreator ? duel?.opponent : duel?.creator;

    const achievement = {
      id: `duel_${duel?.id}`,
      type: ACHIEVEMENT_TYPES?.DUEL_WIN,
      title: '🏆 Duel Victory!',
      description: `You defeated ${opponent?.full_name} in ${duel?.title}`,
      emoji: '🎉',
      metric: duel?.metric,
      value: userProgress,
      targetValue: duel?.target_value,
      opponentValue: opponentProgress,
      opponentName: opponent?.full_name,
      duelTitle: duel?.title,
      duelDescription: duel?.description,
      durationDays: duel?.duration_days,
      stats: {
        totalWins: stats?.duels_won || 0,
        totalLosses: stats?.duels_lost || 0,
        totalCompleted: stats?.duels_completed || 0,
        winRate: stats?.duels_completed > 0 
          ? Math.round((stats?.duels_won / stats?.duels_completed) * 100) 
          : 0
      },
      streak: streak,
      date: new Date()?.toISOString()
    };

    // Mark as shown
    markAchievementShown(userId, ACHIEVEMENT_TYPES?.DUEL_WIN, duel?.id);

    return achievement;
  },

  /**
   * Check for goal completion achievement
   */
  async checkGoalCompletion(userId, goal) {
    if (!goal || goal?.current < goal?.target) {
      return null;
    }

    const goalId = `${goal?.metric}_${goal?.target}_${new Date()?.toISOString()?.split('T')?.[0]}`;

    // Check if already shown today
    if (isAchievementShown(userId, ACHIEVEMENT_TYPES?.GOAL_COMPLETE, goalId)) {
      return null;
    }

    const streak = await calculateStreak(userId);

    const metricLabels = {
      steps: 'Steps Goal',
      calories: 'Calorie Goal',
      water: 'Hydration Goal',
      sleep: 'Sleep Goal'
    };

    const metricEmojis = {
      steps: '🚶',
      calories: '🔥',
      water: '💧',
      sleep: '🌙'
    };

    const achievement = {
      id: goalId,
      type: ACHIEVEMENT_TYPES?.GOAL_COMPLETE,
      title: `${metricEmojis?.[goal?.metric]} ${metricLabels?.[goal?.metric]} Achieved!`,
      description: `You reached your ${goal?.target} ${goal?.unit} goal!`,
      emoji: metricEmojis?.[goal?.metric],
      metric: goal?.metric,
      value: goal?.current,
      targetValue: goal?.target,
      unit: goal?.unit,
      streak: streak,
      date: new Date()?.toISOString()
    };

    // Mark as shown
    markAchievementShown(userId, ACHIEVEMENT_TYPES?.GOAL_COMPLETE, goalId);

    return achievement;
  },

  /**
   * Check for milestone achievement (converts milestone to achievement card)
   */
  async checkMilestoneAchievement(userId, milestone) {
    if (!milestone) return null;

    const milestoneId = `${milestone?.metric}_${milestone?.value}_${milestone?.date}`;

    // Check if already shown
    if (isAchievementShown(userId, ACHIEVEMENT_TYPES?.MILESTONE, milestoneId)) {
      return null;
    }

    const streak = await calculateStreak(userId);

    const achievement = {
      id: milestoneId,
      type: ACHIEVEMENT_TYPES?.MILESTONE,
      title: milestone?.title,
      description: milestone?.message,
      emoji: milestone?.emoji,
      metric: milestone?.metric,
      value: milestone?.value,
      streak: streak,
      date: milestone?.date
    };

    // Mark as shown
    markAchievementShown(userId, ACHIEVEMENT_TYPES?.MILESTONE, milestoneId);

    return achievement;
  },

  /**
   * Check for duel win streak achievement
   */
  async checkDuelStreak(userId) {
    try {
      const stats = await duelActivityService?.getUserDuelStats(userId);
      const totalWins = stats?.duels_won || 0;

      // Check for streak milestones (3, 5, 10 wins)
      const streakMilestones = [3, 5, 10, 20];
      let achievedStreak = null;

      for (const milestone of streakMilestones) {
        if (totalWins >= milestone) {
          if (!isAchievementShown(userId, ACHIEVEMENT_TYPES?.DUEL_STREAK, milestone)) {
            achievedStreak = milestone;
          }
        }
      }

      if (!achievedStreak) return null;

      const streakTitles = {
        3: '🔥 Triple Threat',
        5: '⚡ Five-Star Champion',
        10: '💎 Perfect Ten',
        20: '👑 Legendary Warrior'
      };

      const streakDescriptions = {
        3: 'Won 3 duels in a row!',
        5: 'Incredible 5-win streak!',
        10: 'Unstoppable 10-win streak!',
        20: 'Legendary 20-win streak!'
      };

      const achievement = {
        id: `streak_${achievedStreak}`,
        type: ACHIEVEMENT_TYPES?.DUEL_STREAK,
        title: streakTitles?.[achievedStreak],
        description: streakDescriptions?.[achievedStreak],
        emoji: '🏆',
        value: achievedStreak,
        stats: {
          totalWins: stats?.duels_won || 0,
          totalLosses: stats?.duels_lost || 0,
          totalCompleted: stats?.duels_completed || 0,
          winRate: stats?.duels_completed > 0 
            ? Math.round((stats?.duels_won / stats?.duels_completed) * 100) 
            : 0
        },
        date: new Date()?.toISOString()
      };

      // Mark as shown
      markAchievementShown(userId, ACHIEVEMENT_TYPES?.DUEL_STREAK, achievedStreak);

      return achievement;
    } catch (error) {
      console.error('Error checking duel streak:', error);
      return null;
    }
  },

  /**
   * Reset achievement tracking (for testing)
   */
  resetAchievements(userId) {
    Object.keys(localStorage)?.forEach(key => {
      if (key?.startsWith(`achievement_${userId}_`)) {
        localStorage.removeItem(key);
      }
    });
  }
};

export default achievementCardService;
